import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, FileDown, Mail, Loader2, CheckCircle2, BookOpen, Clock, X, Fingerprint, PlusCircle, Timer } from 'lucide-react';
import { PlanningAfDialog } from './planning/PlanningAfDialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { getAuthToken } from '../lib/authToken';
import { saveBlob } from '../lib/download';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUT_STYLE = {
  formation: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  travail_presume: 'bg-slate-100 text-slate-600 border-slate-200',
  conge_paye: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  off: 'bg-gray-200 text-gray-600 border-gray-300',
  maladie: 'bg-red-100 text-red-700 border-red-200',
  inactif: 'bg-amber-50 text-amber-700 border-amber-200',
  non_affecte: 'bg-white text-slate-400 border-dashed border-slate-200',
  weekend: 'bg-slate-50 text-slate-300 border-slate-100',
};
const fmtH = (h) => `${Number.isInteger(h) ? h : Number(h).toFixed(1)} h`;

export const ActiviteCollaborateurPanel = ({ formateurId, mode = 'admin', initialMonth }) => {
  const [month, setMonth] = useState(initialMonth || new Date());
  const [planningAf, setPlanningAf] = useState(null); // { dossierId, date } -> dialogue planning pedagogique de l'AF
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);
  const [emails, setEmails] = useState('');
  const [pointageDay, setPointageDay] = useState(null);
  const [pointageForm, setPointageForm] = useState({ heure_debut: '09:00', heure_fin: '17:00', pause_minutes: 60, note: '' });
  const [extraDay, setExtraDay] = useState(null);
  const [extraForm, setExtraForm] = useState({ heures: '1', note: '' });
  const [savingExtra, setSavingExtra] = useState(false);
  const mois = format(month, 'yyyy-MM');
  const headers = { Authorization: `Bearer ${getAuthToken()}` };
  const base = mode === 'admin' ? `${API_URL}/collaborateurs/${formateurId}/activite` : `${API_URL}/mon-activite`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(base, { headers, params: { mois } });
      setData(res.data);
      setEmails(res.data.formateur?.email || '');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de chargement'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, mois]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const setJour = async (date, type_affectation) => {
    try {
      const url = mode === 'admin' ? `${base}/${date}` : `${API_URL}/mon-activite/${date}`;
      await axios.put(url, { type_affectation: type_affectation || null }, { headers });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const savePointage = async () => {
    try {
      await axios.put(`${API_URL}/mon-activite/pointage/${pointageDay}`, pointageForm, { headers });
      toast.success('Pointage enregistré');
      setPointageDay(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openExtra = (j) => {
    setExtraForm({ heures: j.extra?.heures != null ? String(j.extra.heures) : '1', note: j.extra?.note || '' });
    setExtraDay(j.date);
  };

  const saveExtra = async (supprimer = false) => {
    const heures = supprimer ? 0 : parseFloat(String(extraForm.heures).replace(',', '.'));
    if (!supprimer && (!heures || heures <= 0)) { toast.error('Indiquez un nombre d\'heures supérieur à 0'); return; }
    setSavingExtra(true);
    try {
      await axios.put(`${base}/${extraDay}/extra`, { heures, note: extraForm.note }, { headers });
      toast.success(supprimer ? 'Heures extra supprimées' : `+${heures} h extra enregistrées`);
      setExtraDay(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingExtra(false); }
  };

  const downloadPdf = async () => {
    if (mode !== 'admin') return;
    setExporting('pdf');
    try {
      const res = await axios.get(`${base}.pdf`, { headers, params: { mois }, responseType: 'blob' });
      saveBlob(res.data, `activite_${data?.formateur?.nom || ''}_${mois}.pdf`);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur lors de la génération du PDF'); }
    finally { setExporting(null); }
  };

  const sendMail = async () => {
    await downloadPdf();
    const t = data.totaux;
    const subject = `Relevé d'activité ${format(month, 'MMMM yyyy', { locale: fr })} – ${data.formateur.nom} ${data.formateur.prenom}`;
    const body = ['Bonjour,', '', `Veuillez trouver ci-joint le relevé d'activité de ${data.formateur.nom} ${data.formateur.prenom} pour ${format(month, 'MMMM yyyy', { locale: fr })}.`, '',
      `Heures totales : ${t.heures_total} h (formation : ${t.heures_formation} h sur ${t.jours_formation} j · autres activités : ${t.heures_autres} h)`,
      `Congés payés : ${t.jours_conge} j · Maladie/OFF : ${t.jours_maladie + t.jours_off} j · Non affectés : ${t.jours_non_affectes} j`, '', 'Cordialement,'].join('\n');
    window.location.href = `mailto:${encodeURIComponent(emails.split(/[;,\s]+/).filter(Boolean).join(','))}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setMailOpen(false);
  };

  if (!data) return <div className="p-6 text-center text-slate-400 text-sm">{loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Aucune donnée'}</div>;
  const t = data.totaux;
  const activites = data.activites.filter(a => !a.legacy);

  return (
    <div className="space-y-4" data-testid="activite-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setMonth(subMonths(month, 1))} data-testid="activite-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold text-slate-800 capitalize min-w-[140px] text-center" data-testid="activite-mois">{format(month, 'MMMM yyyy', { locale: fr })}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setMonth(addMonths(month, 1))} data-testid="activite-next"><ChevronRight className="w-4 h-4" /></Button>
          <Input type="month" value={mois} onChange={(e) => e.target.value && setMonth(new Date(`${e.target.value}-01`))} className="h-8 w-40 text-xs" data-testid="activite-month-input" />
        </div>
        {mode === 'admin' && (
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="text-xs" onClick={downloadPdf} disabled={!!exporting} data-testid="activite-pdf">
              {exporting === 'pdf' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />} PDF
            </Button>
            <Button type="button" size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => setMailOpen(true)} data-testid="activite-mail"><Mail className="w-3.5 h-3.5 mr-1" /> Envoyer par email</Button>
          </div>
        )}
      </div>

      {data.formateur.gerant && <p className="text-xs text-slate-500 bg-slate-50 border rounded-md p-2" data-testid="activite-gerant-note">Statut Gérant : non soumis aux activités journalières ni à une durée de contrat. Seules ses interventions en formation sont listées.</p>}
      {data.periodes_actives.length > 0 && (
        <p className="text-[11px] text-slate-500" data-testid="activite-periodes">Période(s) active(s) : {data.periodes_actives.map((p, i) => <span key={i} className="mr-2">{p.debut || '…'} → {p.fin || 'en cours'}{p.motif_fin && p.fin ? ` (${p.motif_fin})` : ''}</span>)}</p>
      )}

      <div className="grid grid-cols-3 md:grid-cols-7 gap-2" data-testid="activite-totaux">
        {[[fmtH(t.heures_total), 'Heures totales', 'bg-indigo-50 border-indigo-200', 'total'], [fmtH(t.heures_formation), `Formation (${t.jours_formation} j)`, 'bg-blue-50 border-blue-200', 'formation'], [fmtH(t.heures_autres), 'Autres activités', 'bg-slate-50 border-slate-200', 'autres'], [fmtH(t.heures_extra_saisies || 0), 'Heures extra', 'bg-violet-50 border-violet-200', 'extra'], [t.jours_conge, 'Congés payés', 'bg-emerald-50 border-emerald-200', 'conge'], [t.jours_maladie + t.jours_off, 'Maladie / OFF', 'bg-red-50 border-red-200', 'off'], [t.jours_non_affectes, 'Non affectés', 'bg-amber-50 border-amber-200', 'nonaffecte']].map(([v, l, cls, id]) => (
          <div key={l} className={`rounded-lg border p-2 text-center ${cls}`}><p className="text-base font-bold text-slate-800" data-testid={`activite-kpi-${id}`}>{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
        ))}
      </div>

      <div className="border rounded-lg divide-y max-h-[520px] overflow-y-auto" data-testid="activite-jours">
        {data.jours.map(j => {
          const d = new Date(`${j.date}T00:00:00`);
          const editable = !j.weekend && j.af.length === 0 && j.statut !== 'inactif';
          return (
            <div key={j.date} className={`flex items-center gap-3 px-3 py-1.5 text-xs ${j.weekend ? 'bg-slate-50/60' : ''}`} data-testid={`activite-jour-${j.date}`}>
              <span className={`w-16 shrink-0 font-medium ${j.weekend ? 'text-slate-300' : 'text-slate-700'}`}>{format(d, 'EEE dd', { locale: fr })}</span>
              <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                {j.af.map(a => (
                  <span key={a.dossier_id} role={mode === 'admin' ? 'button' : undefined} tabIndex={mode === 'admin' ? 0 : undefined} onClick={() => mode === 'admin' && setPlanningAf({ dossierId: a.dossier_id, date: j.date })} onKeyDown={(e) => { if (mode === 'admin' && (e.key === 'Enter' || e.key === ' ')) setPlanningAf({ dossierId: a.dossier_id, date: j.date }); }} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${STATUT_STYLE.formation} ${mode === 'admin' ? 'cursor-pointer hover:bg-indigo-100 hover:border-indigo-300 transition-colors' : ''}`} title={mode === 'admin' ? `${a.intitule} — cliquer pour ouvrir le planning pédagogique` : a.intitule} data-testid={`activite-af-${j.date}-${a.dossier_id}`}>
                    <BookOpen className="w-3 h-3" /> {a.code} <span className="text-indigo-400 truncate max-w-[180px]">{a.intitule}</span>
                    {a.demi_journee !== 'journee' && <span className="text-[9px]">½ j</span>}
                    {a.cahier_saisi ? <CheckCircle2 className="w-3 h-3 text-emerald-600" title="Cahier de texte saisi" /> : <span className="text-[9px] text-amber-600" title="Cahier non saisi">cahier ?</span>}
                  </span>
                ))}
                {!j.af.length && !j.weekend && (
                  editable ? (
                    <select value={j.hp?.type_affectation || ''} onChange={(e) => setJour(j.date, e.target.value)} className={`h-6 rounded border px-1 text-[11px] ${STATUT_STYLE[j.statut] || 'bg-white border-slate-200 text-slate-700'}`} data-testid={`activite-select-${j.date}`}>
                      <option value="">{j.statut === 'travail_presume' ? 'Travail admin./commercial (présumé)' : '— Non affecté —'}</option>
                      {activites.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  ) : <span className={`px-2 py-0.5 rounded border ${STATUT_STYLE[j.statut] || ''}`}>{j.label}</span>
                )}
                {j.hp?.type_affectation && editable && <button type="button" onClick={() => setJour(j.date, null)} className="text-slate-300 hover:text-red-500" title="Retirer"><X className="w-3 h-3" /></button>}
                {j.pointage && <span className="inline-flex items-center gap-1 text-[10px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5" title={j.pointage.note || ''}><Clock className="w-3 h-3" /> {j.pointage.heure_debut}–{j.pointage.heure_fin} ({j.pointage.heures} h)</span>}
                {j.extra?.heures > 0 && (
                  <button type="button" onClick={() => mode === 'admin' && openExtra(j)} className={`inline-flex items-center gap-1 text-[10px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 max-w-[260px] ${mode === 'admin' ? 'hover:bg-violet-100' : 'cursor-default'}`} title={j.extra.note || 'Heures extra'} data-testid={`activite-extra-${j.date}`}>
                    <Timer className="w-3 h-3 shrink-0" /> +{fmtH(j.extra.heures)} extra{j.extra.note && <span className="text-violet-500 truncate">· {j.extra.note}</span>}
                  </button>
                )}
                {j.indices.length > 0 && <span className="inline-flex items-center gap-1 text-[10px] text-slate-400" title={`Indices de présence : ${[...new Set(j.indices)].join(', ')}`}><Fingerprint className="w-3 h-3" /> {[...new Set(j.indices)].join(', ')}</span>}
              </div>
              {mode === 'self' && !j.weekend && j.statut !== 'inactif' && (
                <button type="button" onClick={() => { setPointageDay(j.date); setPointageForm({ heure_debut: j.pointage?.heure_debut || '09:00', heure_fin: j.pointage?.heure_fin || '17:00', pause_minutes: j.pointage?.pause_minutes ?? 60, note: j.pointage?.note || '' }); }} className="text-[10px] text-indigo-600 hover:underline shrink-0" data-testid={`pointage-btn-${j.date}`}>{j.pointage ? 'Modifier' : 'Pointer'}</button>
              )}
              {mode === 'admin' && !(j.extra?.heures > 0) && (
                <button type="button" onClick={() => openExtra(j)} className="text-slate-300 hover:text-violet-600 shrink-0 transition-colors" title="Ajouter des heures extra" aria-label={`Ajouter des heures extra le ${j.date}`} data-testid={`activite-extra-add-${j.date}`}>
                  <PlusCircle className="w-3.5 h-3.5" />
                </button>
              )}
              <span className={`w-12 text-right shrink-0 font-semibold ${j.heures ? 'text-slate-800' : 'text-slate-300'}`} data-testid={`activite-heures-${j.date}`}>{j.heures ? fmtH(j.heures) : '—'}</span>
            </div>
          );
        })}
      </div>

      <PlanningAfDialog open={!!planningAf} dossierId={planningAf?.dossierId} highlightDate={planningAf?.date} onOpenChange={(o) => { if (!o) setPlanningAf(null); }} onSaved={fetchData} />

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent className="max-w-md" data-testid="activite-mail-dialog">
          <DialogHeader><DialogTitle>Envoyer le relevé d'activité</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Destinataires (collaborateur, comptable, paie…) séparés par des virgules</Label>
            <Input value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="activite-mail-emails" />
            <p className="text-[11px] text-slate-500">Le PDF sera téléchargé puis votre messagerie s'ouvrira avec le texte prérempli : joignez le PDF téléchargé.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setMailOpen(false)}>Annuler</Button>
            <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={sendMail} disabled={!emails.trim()} data-testid="activite-mail-send">Ouvrir ma messagerie</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!extraDay} onOpenChange={(o) => !o && setExtraDay(null)}>
        <DialogContent className="max-w-sm" data-testid="extra-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Timer className="w-4 h-4 text-violet-600" /> Heures extra du {extraDay && format(new Date(`${extraDay}T00:00:00`), 'EEEE dd MMMM', { locale: fr })}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Heures ajoutées en plus de la formation ou de l'activité du jour (préparation, déplacement, réunion…). Elles comptent dans les heures totales du mois.</p>
          <div className="grid grid-cols-[110px_1fr] gap-3">
            <div><Label className="text-xs">Heures</Label><Input type="number" min="0.25" max="24" step="0.25" value={extraForm.heures} onChange={(e) => setExtraForm({ ...extraForm, heures: e.target.value })} data-testid="extra-heures" /></div>
            <div><Label className="text-xs">Note (facultatif)</Label><Input value={extraForm.note} onChange={(e) => setExtraForm({ ...extraForm, note: e.target.value })} placeholder="Ex. préparation supports, réunion client…" data-testid="extra-note" /></div>
          </div>
          <DialogFooter className="gap-2">
            {data.jours.find(j => j.date === extraDay)?.extra?.heures > 0 && (
              <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700 mr-auto" onClick={() => saveExtra(true)} disabled={savingExtra} data-testid="extra-delete">Supprimer</Button>
            )}
            <Button type="button" variant="outline" onClick={() => setExtraDay(null)}>Annuler</Button>
            <Button type="button" className="bg-violet-600 hover:bg-violet-700" onClick={() => saveExtra(false)} disabled={savingExtra} data-testid="extra-save">{savingExtra ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pointageDay} onOpenChange={(o) => !o && setPointageDay(null)}>
        <DialogContent className="max-w-sm" data-testid="pointage-dialog">
          <DialogHeader><DialogTitle>Pointage du {pointageDay}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Début</Label><Input type="time" value={pointageForm.heure_debut} onChange={(e) => setPointageForm({ ...pointageForm, heure_debut: e.target.value })} data-testid="pointage-debut" /></div>
            <div><Label className="text-xs">Fin</Label><Input type="time" value={pointageForm.heure_fin} onChange={(e) => setPointageForm({ ...pointageForm, heure_fin: e.target.value })} data-testid="pointage-fin" /></div>
            <div><Label className="text-xs">Pause (min)</Label><Input type="number" value={pointageForm.pause_minutes} onChange={(e) => setPointageForm({ ...pointageForm, pause_minutes: parseInt(e.target.value) || 0 })} data-testid="pointage-pause" /></div>
          </div>
          <div><Label className="text-xs">Note</Label><Input value={pointageForm.note} onChange={(e) => setPointageForm({ ...pointageForm, note: e.target.value })} /></div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={async () => { await axios.put(`${API_URL}/mon-activite/pointage/${pointageDay}`, {}, { headers }); setPointageDay(null); fetchData(); }}>Supprimer</Button>
            <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={savePointage} data-testid="pointage-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
